import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../api';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';

const STATUS_OPTIONS = ['all', 'no_active_signal', 'active_signal', 'data_error'];

const formatTs = (ts) => {
  if (!ts) return '–';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return String(ts);
  return d.toLocaleString('en-IN', { hour12: false });
};

const prettify = (val) => {
  if (!val) return '–';
  return String(val)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase());
};

const toText = (val) => {
  if (val === null || val === undefined || val === '') return '–';
  if (typeof val === 'object') {
    try {
      return JSON.stringify(val);
    } catch {
      return String(val);
    }
  }
  return String(val);
};

const pickFirstText = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const text = String(value).trim();
    if (text && text.toLowerCase() !== 'null' && text.toLowerCase() !== 'undefined') {
      return text;
    }
  }
  return '';
};

const extractProgressMessage = (progress) => {
  if (!Array.isArray(progress)) return '';
  for (const step of progress) {
    const msg = pickFirstText(step?.message, step?.reason, step?.details);
    if (msg) return msg;
  }
  return '';
};

const getRunMessage = (run = {}) => {
  const direct = pickFirstText(run.message);
  if (direct) return direct;

  const payloadSignal = pickFirstText(
    run?.signal_payload?.signal_type,
    run?.signal_payload?.signal,
    run?.signal_payload?.action
  );
  if (run.status === 'active_signal' && payloadSignal) {
    return `Active signal generated: ${payloadSignal}`;
  }

  const strategyMsg = extractProgressMessage(run.strategy_progress);
  if (strategyMsg) return strategyMsg;

  if (run.status === 'active_signal') return 'Active signal generated.';
  if (run.status === 'no_active_signal') return 'No active signal generated for this run.';
  if (run.status === 'data_error') return 'Analyzer run ended with a data error.';
  return 'No message';
};

const getSignalIdentity = (run = {}) => {
  const payload = run?.signal_payload;
  if (!payload || typeof payload !== 'object') return '';

  const explicitId = pickFirstText(
    payload.signal_id,
    payload.id,
    payload.signal_hash,
    payload.dedup_key,
    payload.uuid
  );
  if (explicitId) return explicitId;

  const strategy = pickFirstText(payload.strategy_type, payload.strategy, payload.strategy_name, 'unknown_strategy');
  const side = pickFirstText(payload.signal_type, payload.signal, payload.action, 'unknown_side');
  const entry = pickFirstText(payload.entry_price, payload.entry, payload.entry_point, 'na');
  const sl = pickFirstText(payload.stop_loss, payload.sl, 'na');
  const t1 = pickFirstText(payload.target_1, payload.target1, 'na');
  const t2 = pickFirstText(payload.target_2, payload.target2, 'na');
  const t3 = pickFirstText(payload.target_3, payload.target3, 'na');

  return `${strategy}|${side}|${entry}|${sl}|${t1}|${t2}|${t3}`;
};

const toStrategyLabel = (value) => {
  const text = pickFirstText(value);
  if (!text) return '–';
  return text
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (m) => m.toUpperCase());
};

const getSignalStrategy = (run = {}) => {
  const payload = run?.signal_payload;
  if (payload && typeof payload === 'object') {
    const direct = pickFirstText(payload.strategy_type, payload.strategy, payload.strategy_name);
    if (direct) return toStrategyLabel(direct);
  }

  const progress = Array.isArray(run?.strategy_progress) ? run.strategy_progress : [];
  const activeStep = progress.find((step) => String(step?.status || '').toLowerCase() === 'active');
  if (activeStep) {
    const strategy = pickFirstText(activeStep.strategy_name, activeStep.strategy, activeStep.name);
    if (strategy) return toStrategyLabel(strategy);
  }

  return run?.status === 'active_signal' ? 'Unspecified Active Strategy' : 'No Active Strategy';
};

/** Show JSON or a clear hint when the API/database value is null (not the same as `{}`). */
const formatStoredJson = (value, nullHint) => {
  if (value === null || value === undefined) {
    return <span className="text-gray-500">{nullHint}</span>;
  }
  if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) {
    return <span className="text-gray-500">Empty object stored in DB.</span>;
  }
  return JSON.stringify(value, null, 2);
};

const formatIndicatorSummary = (li) => {
  if (!li || typeof li !== 'object') return null;
  const chunks = [];
  if (li.rsi_14 != null && !Number.isNaN(Number(li.rsi_14))) {
    const r = Number(li.rsi_14).toFixed(1);
    chunks.push(li.rsi_14_forming_bar != null && li.rsi_14_forming_bar !== undefined ? `RSI ${r} (closed bar)` : `RSI ${r}`);
  }
  if (li.ema_9 != null && !Number.isNaN(Number(li.ema_9))) chunks.push(`EMA9 ${Number(li.ema_9).toFixed(1)}`);
  if (li.vwap != null && !Number.isNaN(Number(li.vwap))) chunks.push(`VWAP ${Number(li.vwap).toFixed(1)}`);
  if (li.supertrend_1 != null && !Number.isNaN(Number(li.supertrend_1))) chunks.push(`ST ${Number(li.supertrend_1).toFixed(1)}`);
  return chunks.length ? chunks.join(' · ') : null;
};

const csvEscape = (value) => {
  const str = value == null ? '' : String(value);
  const escaped = str.replace(/"/g, '""');
  return `"${escaped}"`;
};

const toStrategyKey = (entry = {}) => {
  const raw = entry.strategy_type || entry.strategy || entry.strategy_name || entry.name || 'unknown_strategy';
  return String(raw).trim().toLowerCase().replace(/\s+/g, '_');
};

const toReasonText = (entry = {}) => {
  return String(entry.reason || entry.details || entry.message || entry.status || entry.result || '').trim();
};

const looksBlocked = (entry = {}) => {
  const statusText = String(entry.status || entry.result || '').toLowerCase();
  const reasonText = toReasonText(entry).toLowerCase();
  const text = `${statusText} ${reasonText}`;
  return /(block|skip|fail|reject|no\s+signal|not\s+qualif|cooldown|neutral)/.test(text);
};

const makeRecommendation = (reasons, strategyKey) => {
  const text = reasons.join(' ').toLowerCase();
  if (text.includes('cooldown')) {
    return 'Frequent cooldown blocks. Consider strategy-specific cooldown tuning or adaptive cooldown by volatility/time window.';
  }
  if (text.includes('neutral') || text.includes('bias')) {
    return 'Bias filter is frequently preventing entries. Review neutral-bias handling and confirmation logic around trend transitions.';
  }
  if (text.includes('vix') || text.includes('volatil')) {
    return 'Volatility filters appear restrictive. Revisit VIX-dependent thresholds and strategy activation matrix by regime.';
  }
  if (text.includes('no qualifying') || text.includes('master strategy')) {
    return 'Master strategy misses are high. Backtest trigger thresholds (Inside Bar/VWAP/EMA scalp) and loosen only where precision is acceptable.';
  }
  if (text.includes('data') || text.includes('gap')) {
    return 'Data-quality failures observed. Add stronger candle continuity monitoring and fallback data fetch safeguards.';
  }
  if (strategyKey.includes('pivot')) {
    return 'Pivot strategy is often blocked. Recheck continuation/breakout confirmation strictness and daily cap settings.';
  }
  if (strategyKey.includes('ema')) {
    return 'EMA strategy is often blocked. Review pullback quality thresholds and RSI/volume constraints for missed valid trends.';
  }
  return 'Capture a few blocked examples for this strategy and tune one parameter at a time (threshold/cooldown/filter) with before-vs-after win-rate tracking.';
};

const NiftyAnalysisRunsPage = () => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [status, setStatus] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [limit, setLimit] = useState(100);
  const [selectedRunId, setSelectedRunId] = useState(null);

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      params.set('limit', String(limit));
      if (status !== 'all') params.set('status', status);
      if (fromDate) params.set('from_date', fromDate);
      if (toDate) params.set('to_date', toDate);

      const response = await api.get(`/api/v1/nifty/analysis-runs?${params.toString()}`);
      const nextRuns = response?.data?.runs || [];
      setRuns(nextRuns);
      if (!selectedRunId && nextRuns.length > 0) {
        setSelectedRunId(nextRuns[0].run_id);
      }
      if (selectedRunId && !nextRuns.find((r) => r.run_id === selectedRunId)) {
        setSelectedRunId(nextRuns[0]?.run_id || null);
      }
    } catch (err) {
      console.error('Failed to fetch analysis runs', err);
      setError(err?.response?.data?.detail || 'Failed to load analysis runs');
      setRuns([]);
      setSelectedRunId(null);
    } finally {
      setLoading(false);
    }
  }, [fromDate, limit, selectedRunId, status, toDate]);

  useEffect(() => {
    fetchRuns();
  }, [fetchRuns]);

  const selectedRun = useMemo(
    () => runs.find((r) => r.run_id === selectedRunId) || null,
    [runs, selectedRunId]
  );

  const summary = useMemo(() => {
    const base = {
      total: runs.length,
      noActiveSignal: 0,
      activeSignal: 0,
      uniqueActiveSignals: 0,
      activeRunToUniqueRatio: '–',
      dataError: 0,
      topNoSignalReason: '–',
    };

    const reasonCount = {};
    const uniqueSignalKeys = new Set();
    runs.forEach((row) => {
      if (row.status === 'no_active_signal') {
        base.noActiveSignal += 1;
        const msg = getRunMessage(row);
        reasonCount[msg] = (reasonCount[msg] || 0) + 1;
      } else if (row.status === 'active_signal') {
        base.activeSignal += 1;
        const identity = getSignalIdentity(row);
        if (identity) uniqueSignalKeys.add(identity);
      } else if (row.status === 'data_error') {
        base.dataError += 1;
      }
    });
    base.uniqueActiveSignals = uniqueSignalKeys.size;
    if (base.uniqueActiveSignals > 0) {
      base.activeRunToUniqueRatio = `${(base.activeSignal / base.uniqueActiveSignals).toFixed(1)}x`;
    }

    let topReason = '–';
    let topCount = 0;
    Object.entries(reasonCount).forEach(([reason, count]) => {
      if (count > topCount) {
        topReason = reason;
        topCount = count;
      }
    });
    if (topCount > 0) {
      base.topNoSignalReason = `${topReason} (${topCount})`;
    }
    return base;
  }, [runs]);

  const improvementInsights = useMemo(() => {
    const noSignalRuns = runs.filter((r) => r.status === 'no_active_signal');
    const strategyMap = {};
    const globalReasonCount = {};

    noSignalRuns.forEach((run) => {
      const topMessage = getRunMessage(run);
      globalReasonCount[topMessage] = (globalReasonCount[topMessage] || 0) + 1;

      const progress = Array.isArray(run.strategy_progress) ? run.strategy_progress : [];
      progress.forEach((entry) => {
        if (!looksBlocked(entry)) return;
        const key = toStrategyKey(entry);
        const reason = toReasonText(entry) || 'blocked_without_reason';
        if (!strategyMap[key]) {
          strategyMap[key] = { strategy: key, blockedCount: 0, reasonCount: {} };
        }
        strategyMap[key].blockedCount += 1;
        strategyMap[key].reasonCount[reason] = (strategyMap[key].reasonCount[reason] || 0) + 1;
      });
    });

    const strategyBlockers = Object.values(strategyMap)
      .map((row) => {
        const reasons = Object.entries(row.reasonCount)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([reason, count]) => ({ reason, count }));
        return {
          strategy: row.strategy,
          blockedCount: row.blockedCount,
          topReasons: reasons,
          recommendation: makeRecommendation(reasons.map((r) => r.reason), row.strategy),
        };
      })
      .sort((a, b) => b.blockedCount - a.blockedCount)
      .slice(0, 8);

    const topGlobalReasons = Object.entries(globalReasonCount)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([reason, count]) => ({ reason, count }));

    return {
      sampleSize: noSignalRuns.length,
      strategyBlockers,
      topGlobalReasons,
    };
  }, [runs]);

  const exportImprovementsCsv = useCallback(() => {
    const rows = improvementInsights.strategyBlockers || [];
    if (!rows.length) return;

    const header = [
      'generated_at',
      'sample_no_signal_runs',
      'strategy',
      'blocked_count',
      'top_reason_1',
      'top_reason_1_count',
      'top_reason_2',
      'top_reason_2_count',
      'top_reason_3',
      'top_reason_3_count',
      'recommendation',
    ];

    const now = new Date().toISOString();
    const lines = [header.map(csvEscape).join(',')];

    rows.forEach((row) => {
      const r1 = row.topReasons?.[0] || {};
      const r2 = row.topReasons?.[1] || {};
      const r3 = row.topReasons?.[2] || {};
      const line = [
        now,
        improvementInsights.sampleSize,
        prettify(row.strategy),
        row.blockedCount,
        r1.reason || '',
        r1.count || '',
        r2.reason || '',
        r2.count || '',
        r3.reason || '',
        r3.count || '',
        row.recommendation || '',
      ];
      lines.push(line.map(csvEscape).join(','));
    });

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const datePart = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `nifty_strategy_blockers_${datePart}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [improvementInsights]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      <div className="flex-1 container mx-auto px-4 py-8 max-w-7xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">NIFTY Analyzer Diagnostics</h1>
            <p className="text-gray-600 mt-1">Review no-signal reasons, strategy progress, and active signal quality from saved analyzer runs.</p>
          </div>
          <button
            onClick={fetchRuns}
            disabled={loading}
            className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt === 'all' ? 'All Statuses' : prettify(opt)}
                </option>
              ))}
            </select>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            />
            <input
              type="number"
              min={1}
              max={500}
              value={limit}
              onChange={(e) => setLimit(Math.max(1, Math.min(500, Number(e.target.value) || 1)))}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              placeholder="Limit"
            />
            <button
              onClick={fetchRuns}
              disabled={loading}
              className="px-4 py-2 bg-gray-800 text-white rounded-md text-sm font-medium hover:bg-black disabled:opacity-50"
            >
              Apply Filters
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Runs</p>
            <p className="text-2xl font-bold text-gray-900">{summary.total}</p>
          </div>
          <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
            <p className="text-sm text-gray-500">No Signal</p>
            <p className="text-2xl font-bold text-amber-600">{summary.noActiveSignal}</p>
          </div>
          <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Active Signal</p>
            <p className="text-2xl font-bold text-green-600">{summary.activeSignal}</p>
            <p className="text-xs text-gray-500 mt-1">All active analyzer runs</p>
          </div>
          <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Unique Signals</p>
            <p className="text-2xl font-bold text-teal-600">{summary.uniqueActiveSignals}</p>
            <p className="text-xs text-gray-500 mt-1">Active Runs vs Unique: {summary.activeRunToUniqueRatio}</p>
          </div>
          <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Data Errors</p>
            <p className="text-2xl font-bold text-red-600">{summary.dataError}</p>
          </div>
          <div className="bg-white rounded-lg shadow border border-gray-200 p-4 col-span-2 md:col-span-1">
            <p className="text-sm text-gray-500">Top No-Signal Reason</p>
            <p className="text-sm font-semibold text-gray-900 mt-1">{summary.topNoSignalReason}</p>
          </div>
        </div>

        <section className="mb-6 bg-white rounded-lg shadow border border-gray-200 p-5">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h2 className="text-lg font-semibold text-gray-900">Suggested Improvements</h2>
            <div className="flex items-center gap-3">
              <p className="text-xs text-gray-500">
                Built from {improvementInsights.sampleSize} no-signal runs in current filter
              </p>
              <button
                onClick={exportImprovementsCsv}
                disabled={!improvementInsights.strategyBlockers.length}
                className="px-3 py-1.5 text-xs font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Export CSV
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-md p-4">
              <p className="text-sm font-semibold text-gray-800 mb-2">Top No-Signal Reasons</p>
              {improvementInsights.topGlobalReasons.length === 0 ? (
                <p className="text-sm text-gray-500">Not enough no-signal data for reason analysis.</p>
              ) : (
                <ul className="space-y-2">
                  {improvementInsights.topGlobalReasons.map((item, idx) => (
                    <li key={idx} className="text-sm text-gray-700">
                      <span className="font-medium">{item.count}x</span> - {item.reason}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="border border-gray-200 rounded-md p-4">
              <p className="text-sm font-semibold text-gray-800 mb-2">Strategy Blocker Groups + Recommendations</p>
              {improvementInsights.strategyBlockers.length === 0 ? (
                <p className="text-sm text-gray-500">No blocker clusters found in strategy progress.</p>
              ) : (
                <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                  {improvementInsights.strategyBlockers.map((row, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-md p-3 bg-gray-50">
                      <p className="text-sm font-semibold text-gray-900">
                        {prettify(row.strategy)} ({row.blockedCount} blocks)
                      </p>
                      <ul className="mt-1 text-xs text-gray-700 list-disc list-inside">
                        {row.topReasons.map((r, i) => (
                          <li key={i}>{r.reason} ({r.count}x)</li>
                        ))}
                      </ul>
                      <p className="mt-2 text-xs text-indigo-800">{row.recommendation}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <section className="lg:col-span-1 bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900">Analyzer Runs</h2>
            </div>
            <div className="max-h-[640px] overflow-y-auto">
              {!loading && runs.length === 0 && (
                <p className="p-4 text-sm text-gray-500">No runs found for the selected filters.</p>
              )}
              {runs.map((row) => {
                const indSummary = formatIndicatorSummary(row.live_indicators);
                const resolvedMessage = getRunMessage(row);
                const signalStrategy = getSignalStrategy(row);
                return (
                <button
                  key={row.run_id}
                  onClick={() => setSelectedRunId(row.run_id)}
                  className={`w-full text-left p-4 border-b border-gray-100 hover:bg-gray-50 ${
                    row.run_id === selectedRunId ? 'bg-indigo-50' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-700">{prettify(row.status)}</span>
                    <span className="text-xs text-gray-500">{formatTs(row.timestamp)}</span>
                  </div>
                  <p className="text-sm text-gray-800 mt-1 line-clamp-2">{toText(resolvedMessage)}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    Spot: {toText(row.spot_price)} | VIX: {toText(row.india_vix)} | Bias: {toText(row.market_bias)}
                  </p>
                  <div className="mt-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${
                      row.status === 'active_signal'
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      Signal Strategy: {signalStrategy}
                    </span>
                  </div>
                  {indSummary && (
                    <p className="text-xs text-indigo-700 mt-1 font-medium">{indSummary}</p>
                  )}
                </button>
                );
              })}
            </div>
          </section>

          <section className="lg:col-span-2 space-y-4">
            {!selectedRun && (
              <div className="bg-white rounded-lg shadow border border-gray-200 p-6 text-gray-500">
                Select any run to inspect details.
              </div>
            )}

            {selectedRun && (
              <>
                <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
                  <h2 className="text-lg font-semibold text-gray-900 mb-3">Run Details</h2>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                    <div><span className="text-gray-500">Run ID:</span> <span className="font-medium">{selectedRun.run_id}</span></div>
                    <div><span className="text-gray-500">Status:</span> <span className="font-medium">{prettify(selectedRun.status)}</span></div>
                    <div><span className="text-gray-500">Time:</span> <span className="font-medium">{formatTs(selectedRun.timestamp)}</span></div>
                    <div><span className="text-gray-500">Spot:</span> <span className="font-medium">{toText(selectedRun.spot_price)}</span></div>
                    <div><span className="text-gray-500">VIX:</span> <span className="font-medium">{toText(selectedRun.india_vix)}</span></div>
                    <div><span className="text-gray-500">Regime:</span> <span className="font-medium">{toText(selectedRun.market_regime)}</span></div>
                    <div><span className="text-gray-500">Signal Strategy:</span> <span className="font-medium">{getSignalStrategy(selectedRun)}</span></div>
                  </div>
                  <div className="mt-3">
                    <p className="text-sm text-gray-500">Message</p>
                    <p className="text-sm font-medium text-gray-900">{toText(getRunMessage(selectedRun))}</p>
                    <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                      This text is the <strong>final</strong> no-signal reason after the pipeline runs higher-priority strategies
                      (zone reversal, pivot, EMA pullback, daily SuperTrend, ATH fade) and then the 5m master bundle
                      (Inside Bar / VWAP / EMA scalp). Earlier strategies were evaluated even when this message only names the master bundle.
                    </p>
                  </div>
                </div>

                <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
                  <h3 className="text-base font-semibold text-gray-900 mb-2">Live indicators (5m snapshot)</h3>
                  <p className="text-xs text-gray-500 mb-3">
                    Saved at analysis time. <code className="bg-gray-100 px-1 rounded">rsi_14</code> uses the last{' '}
                    <strong>closed</strong> 5m bar (Wilder RSI is causal — it does not change when only the forming bar&apos;s
                    close is updated with live spot). EMA / SuperTrend / MACD on the latest row <em>do</em> move with that live
                    close. Compare <code className="bg-gray-100 px-1 rounded">rsi_14_forming_bar</code> (when present) for RSI on
                    the current incomplete bar. See <code className="bg-gray-100 px-1 rounded">rsi_14_source</code> in the table.
                  </p>
                  {selectedRun.live_indicators && Object.keys(selectedRun.live_indicators).length > 0 ? (
                    <>
                      <div className="border border-gray-200 rounded-md overflow-hidden max-h-56 overflow-y-auto">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="text-left px-3 py-2 font-medium text-gray-600">Field</th>
                              <th className="text-left px-3 py-2 font-medium text-gray-600">Value</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {Object.keys(selectedRun.live_indicators)
                              .sort()
                              .map((key) => (
                                <tr key={key} className="hover:bg-gray-50">
                                  <td className="px-3 py-1.5 font-mono text-xs text-gray-800">{key}</td>
                                  <td className="px-3 py-1.5 text-xs text-gray-900 break-all">
                                    {selectedRun.live_indicators[key] === null || selectedRun.live_indicators[key] === undefined
                                      ? '–'
                                      : String(selectedRun.live_indicators[key])}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                      <pre className="text-xs bg-gray-50 border border-gray-200 rounded-md p-3 overflow-auto max-h-48 mt-3">
                        {JSON.stringify(selectedRun.live_indicators, null, 2)}
                      </pre>
                    </>
                  ) : (
                    <p className="text-sm text-gray-500">No live indicators stored for this run (e.g. older rows before migration, or data error before indicators were built).</p>
                  )}
                </div>

                <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
                  <h3 className="text-base font-semibold text-gray-900 mb-3">Strategy Progress</h3>
                  {Array.isArray(selectedRun.strategy_progress) && selectedRun.strategy_progress.length > 0 ? (
                    <div className="space-y-2 max-h-80 overflow-y-auto">
                      {selectedRun.strategy_progress.map((s, idx) => (
                        <div key={idx} className="border border-gray-200 rounded-md p-3">
                          <p className="text-sm font-medium text-gray-900">
                            {s.strategy_name || s.strategy || s.name || `Step ${idx + 1}`}
                          </p>
                          <p className="text-xs text-gray-700 mt-1">
                            {(s.status && prettify(s.status)) || (s.result && prettify(s.result)) || 'No explicit status'}
                          </p>
                          {(s.reason || s.details || s.message) && (
                            <p className="text-xs text-gray-600 mt-1">{toText(s.reason || s.details || s.message)}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No strategy progress captured for this run.</p>
                  )}
                </div>

                <div className="bg-white rounded-lg shadow border border-gray-200 p-5">
                  <h3 className="text-base font-semibold text-gray-900 mb-3">Signal / Threshold Context</h3>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Signal Payload</p>
                      <pre className="text-xs bg-gray-50 border border-gray-200 rounded-md p-3 overflow-auto max-h-72 whitespace-pre-wrap">
                        {formatStoredJson(
                          selectedRun.signal_payload,
                          'Not stored (null). Expected for no_active_signal and data_error runs; active_signal runs should have a payload when a signal was saved.'
                        )}
                      </pre>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-2">Dynamic Thresholds</p>
                      <pre className="text-xs bg-gray-50 border border-gray-200 rounded-md p-3 overflow-auto max-h-72 whitespace-pre-wrap">
                        {formatStoredJson(
                          selectedRun.dynamic_thresholds,
                          'Not stored (null). Often happens when AI threshold suggestion is skipped (e.g. async event loop already running) or on early data errors — check backend logs for dynamic threshold warnings.'
                        )}
                      </pre>
                    </div>
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default NiftyAnalysisRunsPage;
